import { z } from "zod";
import type { CommandPhase } from "./stateCommand/pathPolicy";
import { decideStatePath } from "./stateCommand/pathPolicy";
import {
  sanitizeStateString,
  sanitizeStateValue,
} from "./stateCommand/sanitizers";
import { normalizeMhCharacter } from "../domain/actors";

export type StateMutation =
  | { kind: "set"; path: string; value: unknown }
  | { kind: "increment"; path: string; value: number }
  | { kind: "append"; path: string; value: unknown }
  | { kind: "remove"; path: string };

export type StateHelperCommand =
  | {
      kind: "acquireItem";
      key: string;
      name: string;
      quantity: number;
      rarity: number;
      tags: string[];
    }
  | { kind: "consumeItem"; key: string; quantity: number }
  | { kind: "completeTask"; taskId: string }
  | { kind: "failTask"; taskId: string }
  | { kind: "proposeTask"; value: Record<string, unknown> }
  | { kind: "transferZenny"; fromId: string; toId: string; amount: number }
  | { kind: "advanceTime"; hours: number }
  | { kind: "monsterState"; monsterId: string; value: Record<string, unknown> }
  | {
      kind: "upsertCharacter";
      character: ReturnType<typeof normalizeMhCharacter>;
    };

export type ParsedStateCommand = StateMutation | StateHelperCommand;

export interface StateCommandDiagnostic {
  source: string;
  severity: "warning" | "error";
  message: string;
}

export interface StateCommandParseResult {
  visibleText: string;
  commands: ParsedStateCommand[];
  diagnostics: StateCommandDiagnostic[];
  rawBlocks: string[];
}

const jsonHelperSchema = z.discriminatedUnion("op", [
  z.object({
    op: z.literal("add_item"),
    key: z.string(),
    name: z.string(),
    quantity: z.number().int().positive(),
    rarity: z.number().int().min(1).max(10).default(1),
    tags: z.array(z.string()).default(["generic"]),
  }),
  z.object({
    op: z.literal("consume_item"),
    key: z.string(),
    quantity: z.number().int().positive(),
  }),
  z.object({ op: z.literal("complete_task"), taskId: z.string() }),
  z.object({ op: z.literal("fail_task"), taskId: z.string() }),
  z.object({ op: z.literal("propose_task") }).passthrough(),
  z.object({
    op: z.literal("transfer_zenny"),
    fromId: z.string().nullable(),
    toId: z.string().nullable(),
    amount: z.number().int().positive(),
  }),
  z.object({
    op: z.literal("advance_time"),
    hours: z.number().int().min(0).max(8760),
  }),
  z.object({
    op: z.literal("monster_state"),
    monsterId: z.string(),
    name: z.string(),
    rage: z.number().min(0).max(100).optional(),
    fatigue: z.number().min(0).max(100).optional(),
    health: z.number().min(0).max(100).optional(),
    tailSevered: z.boolean().optional(),
    brokenParts: z.array(z.string()).optional(),
  }),
  z.object({
    op: z.literal("upsert_character"),
    character: z.unknown(),
  }),
]);

const jsonMutationSchema = z.discriminatedUnion("op", [
  z.object({ op: z.literal("set"), path: z.string(), value: z.unknown() }),
  z.object({ op: z.literal("increment"), path: z.string(), value: z.number() }),
  z.object({ op: z.literal("append"), path: z.string(), value: z.unknown() }),
  z.object({ op: z.literal("remove"), path: z.string() }),
]);

function extractTaggedBlocks(raw: string): {
  visibleText: string;
  blocks: string[];
} {
  const blocks: string[] = [];
  const visibleText = raw
    .replace(
      /<(?:state|state_commands)>\s*([\s\S]*?)\s*<\/(?:state|state_commands)>/gi,
      (_all, body: string) => {
        blocks.push(body.trim());
        return "";
      },
    )
    .replace(
      /```(?:state|json-state)\s*([\s\S]*?)```/gi,
      (_all, body: string) => {
        blocks.push(body.trim());
        return "";
      },
    )
    .trim();
  return { visibleText, blocks };
}

function splitDslLines(block: string): string[] {
  const lines: string[] = [];
  let current = "",
    quote = "",
    depth = 0;
  for (let index = 0; index < block.length; index++) {
    const char = block[index];
    if (quote) {
      current += char;
      if (char === quote && block[index - 1] !== "\\") quote = "";
      continue;
    }
    if (char === '"' || char === "'") {
      quote = char;
      current += char;
      continue;
    }
    if ("[{(".includes(char)) depth++;
    if ("]})".includes(char)) depth--;
    if ((char === "\n" || char === ";") && depth === 0) {
      if (current.trim()) lines.push(current.trim());
      current = "";
    } else current += char;
  }
  if (current.trim()) lines.push(current.trim());
  return lines;
}

function parseJsonValue(raw: string): unknown {
  const trimmed = raw.trim();
  try {
    return JSON.parse(trimmed);
  } catch {
    if (/^-?\d+(?:\.\d+)?$/.test(trimmed)) return Number(trimmed);
    if (trimmed === "true") return true;
    if (trimmed === "false") return false;
    if (trimmed === "null") return null;
    return trimmed.replace(/^['"]|['"]$/g, "");
  }
}

function translateJsonCommand(value: unknown): ParsedStateCommand {
  const mutation = jsonMutationSchema.safeParse(value);
  if (mutation.success) {
    const item = mutation.data;
    if (item.op === "set")
      return { kind: "set", path: item.path, value: item.value };
    if (item.op === "increment")
      return { kind: "increment", path: item.path, value: item.value };
    if (item.op === "append")
      return { kind: "append", path: item.path, value: item.value };
    return { kind: "remove", path: item.path };
  }
  const helper = jsonHelperSchema.parse(value);
  if (helper.op === "add_item")
    return {
      kind: "acquireItem",
      key: helper.key,
      name: helper.name,
      quantity: helper.quantity,
      rarity: helper.rarity,
      tags: helper.tags,
    };
  if (helper.op === "consume_item")
    return { kind: "consumeItem", key: helper.key, quantity: helper.quantity };
  if (helper.op === "complete_task")
    return { kind: "completeTask", taskId: helper.taskId };
  if (helper.op === "fail_task")
    return { kind: "failTask", taskId: helper.taskId };
  if (helper.op === "advance_time")
    return { kind: "advanceTime", hours: helper.hours };
  if (helper.op === "transfer_zenny")
    return {
      kind: "transferZenny",
      fromId: helper.fromId ?? "guild",
      toId: helper.toId ?? "guild",
      amount: helper.amount,
    };
  if (helper.op === "monster_state") {
    const { op: _op, monsterId, ...value } = helper;
    return { kind: "monsterState", monsterId, value };
  }
  if (helper.op === "upsert_character") {
    const raw = helper.character as Record<string, unknown>;
    const id = typeof raw?.id === "string" ? raw.id : "";
    if (!/^C\d+$/.test(id))
      throw new Error("upsert_character.id 必须是 C 开头的 NPC ID");
    return {
      kind: "upsertCharacter",
      character: normalizeMhCharacter(id, raw),
    };
  }
  const { op: _op, ...task } = helper;
  return { kind: "proposeTask", value: task };
}

function parseDslCommand(line: string): ParsedStateCommand {
  const helperMatch = line.match(
    /^(acquireItem|consumeItem|completeTask|failTask|proposeTask|transferZenny)\s*\((.*)\)$/s,
  );
  if (helperMatch) {
    const args = parseJsonValue(helperMatch[2]);
    const value = Array.isArray(args) ? args : [args];
    if (helperMatch[1] === "acquireItem") {
      const item = z
        .object({
          key: z.string(),
          name: z.string(),
          quantity: z.number().int().positive(),
          rarity: z.number().int().min(1).max(10).default(1),
          tags: z.array(z.string()).default(["generic"]),
        })
        .parse(value[0]);
      return { kind: "acquireItem", ...item };
    }
    if (helperMatch[1] === "consumeItem")
      return {
        kind: "consumeItem",
        key: String(value[0]),
        quantity: z.number().int().positive().parse(value[1]),
      };
    if (helperMatch[1] === "completeTask")
      return { kind: "completeTask", taskId: String(value[0]) };
    if (helperMatch[1] === "failTask")
      return { kind: "failTask", taskId: String(value[0]) };
    if (helperMatch[1] === "proposeTask")
      return {
        kind: "proposeTask",
        value: z.record(z.string(), z.unknown()).parse(value[0]),
      };
    const transfer = z
      .object({
        fromId: z.string().nullable(),
        toId: z.string().nullable(),
        amount: z.number().int().positive(),
      })
      .safeParse(value[0]);
    if (transfer.success)
      return {
        kind: "transferZenny",
        fromId: transfer.data.fromId ?? "guild",
        toId: transfer.data.toId ?? "guild",
        amount: transfer.data.amount,
      };
    return {
      kind: "transferZenny",
      fromId: String(value[0]),
      toId: String(value[1]),
      amount: z.number().int().positive().parse(value[2]),
    };
  }
  const remove = line.match(/^(?:remove|delete)\s+(.+)$/);
  if (remove) return { kind: "remove", path: remove[1].trim() };
  const mutation = line.match(
    /^(?:(?:set|increment|append)\s+)?([\w.[\]"'-]+)\s*(=|\+=|<<)\s*([\s\S]+)$/,
  );
  if (!mutation) throw new Error("无法识别命令语法");
  const [, path, operator, rawValue] = mutation;
  if (operator === "+=")
    return {
      kind: "increment",
      path,
      value: z.number().parse(parseJsonValue(rawValue)),
    };
  if (operator === "<<")
    return { kind: "append", path, value: parseJsonValue(rawValue) };
  return { kind: "set", path, value: parseJsonValue(rawValue) };
}

function validateCommand(
  command: ParsedStateCommand,
  phase: CommandPhase,
): ParsedStateCommand {
  if ("path" in command) {
    const decision = decideStatePath(command.path, phase);
    if (!decision.allowed) throw new Error(decision.reason);
    if (/^player\.vitals\.(health|stamina)$/.test(command.path)) {
      if (command.kind === "remove") throw new Error("玩家体力字段不可删除");
      if (command.kind === "append") throw new Error("玩家体力字段不可追加");
      const value =
        command.kind === "increment" ? command.value : Number(command.value);
      if (!Number.isFinite(value)) throw new Error("玩家体力字段必须为数值");
    }
    if (command.kind === "remove" || command.kind === "increment")
      return command;
    return { ...command, value: sanitizeStateValue(command.value) };
  }
  if (command.kind === "completeTask" && !["main", "entry"].includes(phase))
    throw new Error(`${phase} 阶段无权结算任务`);
  if (command.kind === "failTask" && !["main", "entry"].includes(phase))
    throw new Error(`${phase} 阶段无权判定任务失败`);
  if (command.kind === "proposeTask" && !["main", "misc"].includes(phase))
    throw new Error(`${phase} 阶段无权提案任务`);
  if (
    (command.kind === "acquireItem" || command.kind === "consumeItem") &&
    !["main", "item"].includes(phase)
  )
    throw new Error(`${phase} 阶段无权处理物品`);
  if (command.kind === "transferZenny" && phase !== "main")
    throw new Error(`${phase} 阶段无权转移泽尼`);
  if (command.kind === "advanceTime" && !["main", "misc"].includes(phase))
    throw new Error(`${phase} 阶段无权推进时间`);
  if (
    command.kind === "monsterState" &&
    !["main", "entry", "monster"].includes(phase)
  )
    throw new Error(`${phase} 阶段无权写怪物状态`);
  if (command.kind === "upsertCharacter" && phase !== "entry")
    throw new Error(`${phase} 阶段无权创建或补全 NPC`);
  if (command.kind === "acquireItem")
    return {
      ...command,
      key: sanitizeStateString(command.key, 160),
      name: sanitizeStateString(command.name, 120),
      tags: command.tags.map((tag) => sanitizeStateString(tag, 80)),
    };
  return command;
}

function parseBlock(block: string): unknown[] {
  const normalized = block.replace(/^```(?:json)?|```$/g, "").trim();
  if (normalized.startsWith("[") || normalized.startsWith("{")) {
    const parsed = JSON.parse(normalized) as unknown;
    if (Array.isArray(parsed)) return parsed;
    if (
      parsed &&
      typeof parsed === "object" &&
      Array.isArray((parsed as { commands?: unknown[] }).commands)
    )
      return (parsed as { commands: unknown[] }).commands;
    return [parsed];
  }
  return splitDslLines(normalized);
}

export function parseStateCommandResponse(
  raw: string,
  phase: CommandPhase = "main",
): StateCommandParseResult {
  const extracted = extractTaggedBlocks(raw),
    commands: ParsedStateCommand[] = [],
    diagnostics: StateCommandDiagnostic[] = [];
  if (!extracted.blocks.length && /^[\s\n]*[\[{]/.test(raw)) {
    extracted.blocks.push(raw.trim());
    extracted.visibleText = "";
  }
  for (const block of extracted.blocks) {
    let values: unknown[];
    try {
      values = parseBlock(block);
    } catch (error) {
      diagnostics.push({
        source: block,
        severity: "error",
        message: error instanceof Error ? error.message : String(error),
      });
      continue;
    }
    for (const value of values) {
      try {
        const parsed =
          typeof value === "string"
            ? parseDslCommand(value)
            : translateJsonCommand(value);
        commands.push(validateCommand(parsed, phase));
      } catch (error) {
        diagnostics.push({
          source: typeof value === "string" ? value : JSON.stringify(value),
          severity: "error",
          message: error instanceof Error ? error.message : String(error),
        });
      }
    }
  }
  return {
    visibleText: extracted.visibleText,
    commands,
    diagnostics,
    rawBlocks: extracted.blocks,
  };
}

export function setNestedValue(
  root: unknown,
  path: string,
  value: unknown,
): unknown {
  const segments = path
    .replace(/\[(?:"|')?([^\]"']+)(?:"|')?\]/g, ".$1")
    .replace(/^archive\./, "")
    .split(".")
    .filter(Boolean);
  if (!segments.length) throw new Error("状态路径为空");
  const clone = structuredClone(root) as Record<string, unknown>;
  let cursor: Record<string, unknown> = clone;
  for (const segment of segments.slice(0, -1)) {
    const existing = cursor[segment];
    if (!existing || typeof existing !== "object" || Array.isArray(existing))
      cursor[segment] = {};
    cursor = cursor[segment] as Record<string, unknown>;
  }
  cursor[segments.at(-1) as string] = value;
  return clone;
}
